;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { Badge, type BadgeProps } from '@workspace/ui/components/badge';
import {
  RadioGroup as PrimitiveRadioGroup,
  RadioGroupItem,
} from '@workspace/ui/components/radio-group';
import { cn } from '@workspace/ui/lib';
import { useId } from 'react';

interface RadioOptionBadge {
  text: string;
  variant?: BadgeProps['variant'];
  size?: BadgeProps['size'];
}

interface RadioGroupDescriptionProps {
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export const RadioGroupDescription = ({
  children,
  disabled = false,
  className,
}: RadioGroupDescriptionProps) => {
  return (
    <p
      className={cn(
        'text-sm',
        disabled
          ? 'text-grey-400 dark:text-grey-800 cursor-not-allowed'
          : 'text-grey-700 dark:text-grey-300',
        className
      )}
    >
      {children}
    </p>
  );
};

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  badge?: RadioOptionBadge;
  children?: React.ReactNode;
  disabled?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

interface RadioGroupProps {
  options: RadioOption[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  radioGroupClassName?: string;
  labelClassName?: string;
}

export function RadioGroup({
  options,
  value,
  onChange,
  className,
  disabled = false,
  readOnly = false,
  radioGroupClassName,
  labelClassName,
}: RadioGroupProps) {
  const groupId = useId();

  return (
    <PrimitiveRadioGroup
      value={value}
      onValueChange={onChange}
      className={cn('flex flex-col gap-3', className)}
      disabled={disabled}
      aria-label="Radio options"
    >
      {options.map((option) => {
        const Icon = option.icon;
        const optionId = `${groupId}-${option.value}`;

        return (
          <label
            key={option.value}
            htmlFor={optionId}
            className={cn(
              'group border-grey-400 dark:border-grey-200 flex w-full cursor-pointer rounded-2xl border p-6',
              disabled || option.disabled
                ? 'border-grey-400 dark:border-grey-700 cursor-not-allowed'
                : !readOnly
                  ? 'dark:hover:bg-grey-900 hover:border-transparent hover:bg-pink-50 dark:hover:border-transparent'
                  : 'pointer-events-none cursor-default',
              value === option.value
                ? 'bg-pink-0 border-pink-400 dark:border-pink-400 dark:bg-neutral-200'
                : '',
              option.children ? 'flex-col gap-[14px]' : '',
              option.className,
              labelClassName
            )}
          >
            <div className="flex w-full items-center justify-between gap-2 md:gap-4">
              <div className="flex items-start gap-2">
                {Icon && (
                  <Icon
                    className={cn(
                      'mt-1.5 size-5 stroke-2!',
                      value === option.value
                        ? 'text-pink-400 dark:text-pink-400'
                        : 'text-primary dark:text-neutral-100',
                      disabled || option.disabled ? 'text-grey-400 dark:text-grey-800' : ''
                    )}
                  />
                )}
                <div className="flex flex-col gap-1">
                  <div
                    className={cn(
                      'pointer-events-none flex! h-8 items-center align-middle',
                      option.badge ? 'gap-1.5' : 'gap-[14px]',
                      option.disabled || disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                      className
                    )}
                  >
                    <p
                      className={cn(
                        'text-base leading-tight font-medium text-neutral-200 dark:text-neutral-100',
                        option.disabled || disabled ? 'text-grey-400 dark:text-grey-800' : ''
                      )}
                    >
                      {option.label}
                    </p>
                    {option.badge && (
                      <Badge
                        variant={option.badge.variant ?? 'filled-pink-2'}
                        size={option.badge.size ?? 'sm'}
                        text={option.badge.text}
                      />
                    )}
                  </div>
                  {option.description && (
                    <RadioGroupDescription disabled={disabled || option.disabled}>
                      {option.description}
                    </RadioGroupDescription>
                  )}
                </div>
              </div>

              <RadioGroupItem
                value={option.value}
                id={optionId}
                disabled={option.disabled || disabled}
                readOnly={readOnly}
                className={cn(
                  'hover:border-grey-600 dark:hover:border-grey-200 data-[state=checked]:hover:border-pink-400 dark:data-[state=checked]:hover:border-pink-400',
                  radioGroupClassName
                )}
              />
            </div>

            <div className={`${option.icon ? 'ml-7' : ''}`}>{option.children}</div>
          </label>
        );
      })}
    </PrimitiveRadioGroup>
  );
}
